import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import CompanyBrandingSettings from './CompanyBrandingSettings';
import { getAllConfig, setConfigValues, testGoogleMapsConnection } from '../../../services/systemConfigurationService';


const SystemConfigurationPanel = () => {
  const [activeSection, setActiveSection] = useState('general');
  const [hasChanges, setHasChanges] = useState(false);
  const [config, setConfig] = useState({});
  const [originalConfig, setOriginalConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);

  // Load configuration from database on mount
  useEffect(() => {
    loadConfiguration();
  }, []);

  const loadConfiguration = async () => {
    try {
      setLoading(true);
      const configData = await getAllConfig();
      setConfig(configData);
      setOriginalConfig(JSON.parse(JSON.stringify(configData)));
    } catch (error) {
      console.error('Error loading configuration:', error);
      alert('فشل في تحميل الإعدادات');
    } finally {
      setLoading(false);
    }
  };

  const sections = [
    { id: 'general', label: 'الإعدادات العامة', icon: 'Settings' },
    { id: 'localization', label: 'اللغة والتوطين', icon: 'Globe' },
    { id: 'branding', label: 'العلامة التجارية', icon: 'Building2' },
    { id: 'integrations', label: 'التكاملات الخارجية', icon: 'Link' },
    { id: 'security', label: 'الأمان والخصوصية', icon: 'Shield' },
  ];

  const currencyOptions = [
    { value: 'JOD', label: 'دينار أردني (JOD)' },
    { value: 'SAR', label: 'ريال سعودي (SAR)' },
    { value: 'EGP', label: 'جنيه مصري (EGP)' },
    { value: 'USD', label: 'دولار أمريكي (USD)' },
  ];

  const languageOptions = [
    { value: 'ar', label: 'العربية' },
    { value: 'en', label: 'English' },
  ];

  const dateFormatOptions = [
    { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
    { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
  ];

  const handleChange = (field, value) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await setConfigValues(config);
      setOriginalConfig(JSON.parse(JSON.stringify(config)));
      setHasChanges(false);
      alert('تم حفظ التغييرات بنجاح');
    } catch (error) {
      console.error('Error saving configuration:', error);
      alert(error?.message || 'فشل في حفظ التغييرات');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setConfig(JSON.parse(JSON.stringify(originalConfig)));
    setHasChanges(false);
  };

  const testConnection = async (service) => {
    if (service === 'googleMaps') {
      try {
        setTestingConnection(true);
        const apiKey = config?.google_maps_api_key;
        
        if (!apiKey || apiKey?.trim() === '') {
          alert('يرجى إدخال مفتاح API أولاً');
          return;
        }

        const result = await testGoogleMapsConnection(apiKey);
        
        if (result?.success) {
          alert('✅ ' + result?.message);
        } else {
          alert('❌ ' + result?.message);
        }
      } catch (error) {
        console.error('Error testing connection:', error);
        alert('خطأ في اختبار الاتصال');
      } finally {
        setTestingConnection(false);
      }
    } else {
      // Placeholder for other services
      alert(`جاري اختبار الاتصال بـ ${service}...`);
      setTimeout(() => {
        alert('تم الاتصال بنجاح!');
      }, 1000);
    }
  };

  if (loading) {
    return (
      <div className="bg-[var(--color-card)] rounded-lg border border-[var(--color-border)] shadow-warm p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--color-primary)] mx-auto"></div>
        <p className="text-[var(--color-muted-foreground)] mt-4">جاري تحميل الإعدادات...</p>
      </div>
    );
  }

  return (
    <div className="bg-[var(--color-card)] rounded-lg border border-[var(--color-border)] shadow-warm">
      <div className="p-4 md:p-6 border-b border-[var(--color-border)] flex items-center justify-between gap-4">
        <div>
          <h3 className="text-lg md:text-xl font-heading font-semibold text-[var(--color-foreground)]">
            إعدادات النظام
          </h3>
          <p className="text-sm text-[var(--color-muted-foreground)] mt-1">
            تكوين إعدادات النظام والتكاملات الخارجية
          </p>
        </div>
        {hasChanges && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleReset} disabled={saving}>
              إلغاء
            </Button>
            <Button variant="default" size="sm" iconName="Save" onClick={handleSave} disabled={saving}>
              {saving ? 'جاري الحفظ...' : 'حفظ التغييرات'}
            </Button>
          </div>
        )}
      </div>
      <div className="flex flex-col lg:flex-row">
        <div className="w-full lg:w-64 border-b lg:border-b-0 lg:border-l border-[var(--color-border)] bg-[var(--color-muted)]">
          <nav className="p-2">
            {sections?.map((section) => (
              <button
                key={section?.id}
                onClick={() => setActiveSection(section?.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-right transition-all ${
                  activeSection === section?.id
                    ? 'bg-[var(--color-primary)] text-white shadow-warm'
                    : 'text-[var(--color-foreground)] hover:bg-[var(--color-background)]'
                }`}
              >
                <Icon name={section?.icon} size={20} />
                <span className="text-sm font-medium">{section?.label}</span>
              </button>
            ))}
          </nav>
        </div>

        <div className="flex-1 p-4 md:p-6">
          {activeSection === 'general' && (
            <div className="space-y-4">
              <Input
                label="اسم النظام"
                type="text"
                value={config?.system_name || ''}
                onChange={(e) => handleChange('system_name', e?.target?.value)}
                placeholder="نظام إدارة العقارات"
              />
              <Input
                label="البريد الإلكتروني للنظام"
                type="email"
                value={config?.system_email || ''}
                onChange={(e) => handleChange('system_email', e?.target?.value)}
                placeholder="admin@realestate.com"
              />
              <Input
                label="رقم الهاتف"
                type="tel"
                value={config?.system_phone || ''}
                onChange={(e) => handleChange('system_phone', e?.target?.value)}
                placeholder="+971 50 123 4567"
              />
              <Checkbox
                label="تفعيل وضع الصيانة"
                description="إيقاف الوصول للمستخدمين مؤقتاً"
                checked={config?.maintenance_mode || false}
                onChange={(e) => handleChange('maintenance_mode', e?.target?.checked)}
              />
            </div>
          )}

          {activeSection === 'localization' && (
            <div className="space-y-4">
              <Select
                label="اللغة الافتراضية"
                options={languageOptions}
                value={config?.default_language || 'ar'}
                onChange={(value) => handleChange('default_language', value)}
              />
              <Select
                label="العملة الافتراضية"
                options={currencyOptions}
                value={config?.default_currency || 'JOD'}
                onChange={(value) => handleChange('default_currency', value)}
              />
              <Select
                label="تنسيق التاريخ"
                options={dateFormatOptions}
                value={config?.date_format || 'DD/MM/YYYY'}
                onChange={(value) => handleChange('date_format', value)}
              />
              <Checkbox
                label="استخدام الأرقام العربية"
                description="عرض الأرقام بالصيغة العربية (٠١٢٣٤٥٦٧٨٩)"
                checked={config?.use_arabic_numerals || false}
                onChange={(e) => handleChange('use_arabic_numerals', e?.target?.checked)}
              />
            </div>
          )}

          {activeSection === 'branding' && (
            <CompanyBrandingSettings />
          )}

          {activeSection === 'integrations' && (
            <div className="space-y-6">
              <div className="p-4 bg-[var(--color-muted)] rounded-lg border border-[var(--color-border)]">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Icon name="Map" size={24} color="var(--color-primary)" />
                    <div>
                      <h4 className="text-sm font-semibold text-[var(--color-foreground)]">Google Maps API</h4>
                      <p className="text-xs text-[var(--color-muted-foreground)]">خدمة الخرائط والمواقع</p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    config?.google_maps_api_key && config?.google_maps_api_key?.trim() !== '' ?'bg-[var(--color-success)] text-white' :'bg-[var(--color-muted)] text-[var(--color-muted-foreground)]'
                  }`}>
                    {config?.google_maps_api_key && config?.google_maps_api_key?.trim() !== '' ? 'متصل' : 'غير متصل'}
                  </span>
                </div>
                <Input
                  label="مفتاح API"
                  type="password"
                  value={config?.google_maps_api_key || ''}
                  onChange={(e) => handleChange('google_maps_api_key', e?.target?.value)}
                  placeholder="AIzaSy..."
                />
                <Button
                  variant="outline"
                  size="sm"
                  iconName="RefreshCw"
                  onClick={() => testConnection('googleMaps')}
                  className="mt-3"
                  disabled={testingConnection || !config?.google_maps_api_key || config?.google_maps_api_key?.trim() === ''}
                >
                  {testingConnection ? 'جاري الاختبار...' : 'اختبار الاتصال'}
                </Button>
              </div>

              <div className="p-4 bg-[var(--color-muted)] rounded-lg border border-[var(--color-border)]">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Icon name="MessageSquare" size={24} color="var(--color-success)" />
                    <div>
                      <h4 className="text-sm font-semibold text-[var(--color-foreground)]">WhatsApp Business API</h4>
                      <p className="text-xs text-[var(--color-muted-foreground)]">خدمة المراسلة الفورية</p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    config?.whatsappEnabled ? 'bg-[var(--color-success)] text-white' : 'bg-[var(--color-muted)] text-[var(--color-muted-foreground)]'
                  }`}>
                    {config?.whatsappEnabled ? 'متصل' : 'غير متصل'}
                  </span>
                </div>
                <Input
                  label="رقم الهاتف"
                  type="tel"
                  value={config?.whatsapp_phone || ''}
                  onChange={(e) => handleChange('whatsapp_phone', e?.target?.value)}
                  placeholder="+971501234567"
                />
                <Input
                  label="رمز التحقق"
                  type="password"
                  value={config?.whatsapp_token || ''}
                  onChange={(e) => handleChange('whatsapp_token', e?.target?.value)}
                  placeholder="EAAx..."
                  className="mt-3"
                />
                <Button
                  variant="outline"
                  size="sm"
                  iconName="RefreshCw"
                  onClick={() => testConnection('whatsapp')}
                  className="mt-3"
                >
                  اختبار الاتصال
                </Button>
              </div>

              <div className="p-4 bg-[var(--color-muted)] rounded-lg border border-[var(--color-border)]">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Icon name="Bot" size={24} color="var(--color-accent)" />
                    <div>
                      <h4 className="text-sm font-semibold text-[var(--color-foreground)]">ChatGPT API</h4>
                      <p className="text-xs text-[var(--color-muted-foreground)]">خدمة الردود الآلية</p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    config?.chatgptEnabled ? 'bg-[var(--color-success)] text-white' : 'bg-[var(--color-muted)] text-[var(--color-muted-foreground)]'
                  }`}>
                    {config?.chatgptEnabled ? 'متصل' : 'غير متصل'}
                  </span>
                </div>
                <Input
                  label="مفتاح API"
                  type="password"
                  value={config?.chatgpt_api_key || ''}
                  onChange={(e) => handleChange('chatgpt_api_key', e?.target?.value)}
                  placeholder="sk-..."
                />
                <Button
                  variant="outline"
                  size="sm"
                  iconName="RefreshCw"
                  onClick={() => testConnection('chatgpt')}
                  className="mt-3"
                >
                  اختبار الاتصال
                </Button>
              </div>
            </div>
          )}

          {activeSection === 'security' && (
            <div className="space-y-4">
              <Input
                label="مدة انتهاء الجلسة (دقائق)"
                type="number"
                value={config?.session_timeout || 30}
                onChange={(e) => handleChange('session_timeout', e?.target?.value)}
                placeholder="30"
              />
              <Input
                label="الحد الأقصى لمحاولات تسجيل الدخول"
                type="number"
                value={config?.max_login_attempts || 5}
                onChange={(e) => handleChange('max_login_attempts', e?.target?.value)}
                placeholder="5"
              />
              <Checkbox
                label="تفعيل المصادقة الثنائية"
                description="طلب رمز التحقق عند تسجيل الدخول"
                checked={config?.two_factor_auth || false}
                onChange={(e) => handleChange('two_factor_auth', e?.target?.checked)}
              />
              <Checkbox
                label="تسجيل جميع الأنشطة"
                description="حفظ سجل كامل لجميع العمليات"
                checked={config?.audit_logging || false}
                onChange={(e) => handleChange('audit_logging', e?.target?.checked)}
              />
              <Checkbox
                label="تشفير البيانات الحساسة"
                description="تشفير معلومات العملاء والعقود"
                checked={config?.data_encryption || false}
                onChange={(e) => handleChange('data_encryption', e?.target?.checked)}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SystemConfigurationPanel;